import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertTriangle,
  Upload,
  Loader2,
  CheckCircle2,
  X
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dispute types
const DISPUTE_TYPES = [
  { id: 'quality_issue', label: 'Quality Issue', description: 'Work quality not as expected' },
  { id: 'late_delivery', label: 'Late Delivery', description: 'Work delivered late' },
  { id: 'pricing_dispute', label: 'Pricing Dispute', description: 'Disagreement on pricing' },
  { id: 'communication', label: 'Communication Issue', description: 'Poor or no communication' },
  { id: 'not_as_described', label: 'Not as Described', description: 'Work differs from agreed' },
  { id: 'other', label: 'Other', description: 'Other issues' },
];

/**
 * DisputeModal Component
 * Allows customers to raise disputes on bookings/quotes
 */
const DisputeModal = ({
  open,
  onClose,
  bookingId,
  quoteId,
  providerId,
  providerName,
  onSubmit,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    dispute_type: '',
    subject: '',
    description: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    evidence_urls: [],
  });

  const handleSubmit = async () => {
    if (!formData.dispute_type || !formData.subject || !formData.description || 
        !formData.customer_name || !formData.customer_phone) {
      setError('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/services/disputes`, {
        booking_id: bookingId,
        quote_id: quoteId,
        provider_id: providerId,
        dispute_type: formData.dispute_type,
        subject: formData.subject,
        description: formData.description,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email,
        evidence_urls: formData.evidence_urls,
      });
      
      setSuccess(true);
      onSubmit?.(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSuccess(false);
    setError('');
    setFormData({
      dispute_type: '',
      subject: '',
      description: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      evidence_urls: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Raise a Dispute
          </DialogTitle>
          <DialogDescription>
            Having an issue with {providerName}? Let us help resolve it.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Dispute Submitted</h3>
            <p className="text-gray-600 mb-4">
              We&apos;ve received your dispute and will review it within 24-48 hours.
              You&apos;ll receive updates via phone/email.
            </p>
            <Button onClick={handleClose} data-testid="dispute-success-close">
              Close
            </Button>
          </div>
        ) : (
          <>
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Step 1: Select Type */}
            {step === 1 && (
              <div className="space-y-4 py-4">
                <Label>What type of issue are you facing? *</Label>
                <div className="grid grid-cols-1 gap-2">
                  {DISPUTE_TYPES.map((type) => (
                    <div
                      key={type.id}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.dispute_type === type.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-300'
                      }`}
                      onClick={() => setFormData({ ...formData, dispute_type: type.id })}
                      data-testid={`dispute-type-${type.id}`}
                    >
                      <p className="font-medium text-gray-900">{type.label}</p>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief summary of the issue"
                    data-testid="dispute-subject"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Describe the Issue *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please provide details about what happened..."
                    rows={4}
                    data-testid="dispute-description"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Contact Info */}
            {step === 3 && (
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="customer_name">Your Name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="Enter your name"
                    data-testid="dispute-customer-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_phone">Phone Number *</Label>
                  <Input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    placeholder="07X XXX XXXX"
                    data-testid="dispute-customer-phone"
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_email">Email (Optional)</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="flex justify-between">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)}>
                  Back
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button 
                  onClick={() => setStep(step + 1)}
                  disabled={step === 1 && !formData.dispute_type}
                >
                  Next
                </Button>
              ) : (
                <Button 
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="submit-dispute"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Dispute'
                  )}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DisputeModal;
